import React from 'react';

import { space } from '@opendoor/bricks/theme/eero';
import { Text, Heading, Box, Image, Flex } from '@opendoor/bricks/core';
import { generatePath } from 'react-router-dom';

import {
  CakeContentWrapper,
  CakeContentInner,
} from 'sellDirect/components/legacy/layout/CakeContentWrapper';
import { CakeLayout } from 'sellDirect/components/legacy/layout/CakeLayout';
import { StickyFooter } from 'sellDirect/components/StickyFooter';
import { OdProtosCommonSell_SellDirectOfferRefreshStatus as Status } from '__generated__/globalTypes';
import { useOfferContext } from 'sellDirect/OfferContext';
import { CXA_EMAIL, CXA_EMAIL_NEW, NAVIGATION_HEIGHT } from 'shared/helpers/globals';
import { Redirect, ExternalLink } from 'platform/react-router';
import { getHubOverviewRoute } from 'shared/helpers/routes';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';

export const REFRESH_ERROR_REASON_TITLE = 'refresh-error-reason-title';

interface IRefreshErrorProps {
  refreshStatus?: Status | null;
}

const ContactEmail: React.FC = () => {
  const {
    assertions: { isCepEmailUpdatesEnabled },
  } = useExperimentationContext();
  const email = isCepEmailUpdatesEnabled ? CXA_EMAIL_NEW : CXA_EMAIL;
  return (
    <ExternalLink
      href={`mailto:${email}`}
      analyticsName="cake-refresh-error-email-opendoor"
      aria-label={'Opens new window to email Opendoor'}
    >
      {email}
    </ExternalLink>
  );
};

/** TODO: Add documentation */
export const ContactOnMistake: React.FC = () => (
  <>
    <Text fontSize="s1" lineHeight="s0" fontWeight="bold" mb={4}>
      Think this is a mistake?
    </Text>
    <Text fontSize="s1" lineHeight="s0">
      Email <ContactEmail /> to get help.
    </Text>
  </>
);

const GenericError: React.FC = () => (
  <>
    <Heading size="h3" as="h2" id={REFRESH_ERROR_REASON_TITLE} mb={6}>
      We ran into an issue refreshing your offer
    </Heading>
    <Text fontSize="s1" lineHeight="s0">
      Email <ContactEmail /> so we can help you sort this out.
    </Text>
  </>
);

const AlreadyListed: React.FC = () => (
  <>
    <Heading size="h3" as="h2" id={REFRESH_ERROR_REASON_TITLE} mb={6}>
      Looks like this home is already listed
    </Heading>
    <Text fontSize="s1" lineHeight="s0">
      Your agent can get a new offer by entering the home address at <ContactEmail /> and answering
      a few questions.
    </Text>
  </>
);

const TooSoon: React.FC = () => (
  <>
    <Heading size="h3" as="h2" id={REFRESH_ERROR_REASON_TITLE} mb={6}>
      We ran into an issue refreshing your offer
    </Heading>
    <Text fontSize="s1" lineHeight="s0">
      Wait a few minutes, then try refreshing again. If that doesn’t work, email <ContactEmail />.
    </Text>
  </>
);

const ActiveOffer: React.FC = () => (
  <>
    <Heading size="h3" as="h2" id={REFRESH_ERROR_REASON_TITLE} mb={6}>
      Did someone you know request an offer already?
    </Heading>
    <Text fontSize="s1" lineHeight="s0">
      It looks like we already made an offer for your home. Check with your spouse or family members
      to see if they requested an offer.
    </Text>
  </>
);

const isTerminalGenericError = (refreshStatus: Status) =>
  [
    Status.FAIL_LEAD_NOT_QUALIFIED,
    Status.FAIL_LISTING_DETAILS_NOT_FOUND,
    Status.FAIL_EXISTING_OFFER_NOT_FOUND,
    Status.FAIL_UPCOMING_CALENDLY_MEETING,
    Status.FAIL_INSPECTION_IN_PROGRESS,
    Status.FAIL_OFFER_STATE_NOT_REFRESHABLE,
  ].includes(refreshStatus);

/**
 * Given a refresh status, determine if we should show the contact info
 * for users to email us at contact@opendoor.com
 *
 * This occurs if the `refreshStatus` is NOT:
 * 1. FAIL_REFRESH_REQUESTED_TOO_SOON
 * 2. isTerminalGenericError()
 */
export const getShouldShowBottomContactInfo = (refreshStatus?: Status | null): boolean =>
  !!refreshStatus &&
  refreshStatus !== Status.FAIL_REFRESH_REQUESTED_TOO_SOON &&
  !isTerminalGenericError(refreshStatus);

/** Renders the explanation of a refresh error based on the refresh error status */
export const Reason: React.FC<{ refreshStatus?: Status | null }> = ({ refreshStatus }) => {
  if (refreshStatus === Status.FAIL_HOME_IS_NOW_LISTED) {
    return <AlreadyListed />;
  }
  if (refreshStatus === Status.FAIL_REFRESH_REQUESTED_TOO_SOON) {
    return <TooSoon />;
  }
  if (refreshStatus === Status.FAIL_ACTIVE_OFFER_ON_ADDRESS) {
    return <ActiveOffer />;
  }

  return <GenericError />;
};

/** TODO: Add documentation */
export const RefreshErrorPageContent: React.FC<IRefreshErrorProps> = ({ refreshStatus }) => {
  const shouldShowBottomContactInfo = getShouldShowBottomContactInfo(refreshStatus);
  return (
    <Flex>
      <CakeContentWrapper boxShadow="none">
        <CakeContentInner
          px={0}
          justifyContent="space-between"
          alignItems="flex-start"
          minHeight={`calc(100vh - ${NAVIGATION_HEIGHT})`}
        >
          <Box pt={[5, 7]} px={[5, 7]}>
            <Reason refreshStatus={refreshStatus} />
          </Box>
          <StickyFooter
            width="100%"
            position="relative"
            backgroundColor="blue30"
            px={[7, 9]}
            py={11}
            // @ts-expect-error: TODO @sellDirect
            borderRadius={['60% / 10% 10% 0 0', '60% / 20% 20% 0 0']}
          >
            <Box position="absolute" top={['-55px', '-45px']} right={`${space[9]}px`}>
              <Image
                width="75px"
                src="https://imgdrop.imgix.net/8e26af2ac5dc40a896b68b86527e0e26.svg"
                alt=""
              />
            </Box>
            {shouldShowBottomContactInfo && <ContactOnMistake />}
          </StickyFooter>
        </CakeContentInner>
      </CakeContentWrapper>
    </Flex>
  );
};

/** TODO: Add documentation */
export const RefreshErrorPage: React.FC = () => {
  const { offer, refreshStatus } = useOfferContext();
  const { addressUUID } = useQualificationContext();

  if (!offer?.isExpired) {
    return <Redirect to={generatePath(getHubOverviewRoute(), { addressUUID })} />;
  }
  return (
    <CakeLayout
      loading
      renderContent={() => <RefreshErrorPageContent refreshStatus={refreshStatus} />}
      hideNavBar
    />
  );
};
